// Futuria · Mi Plantel — la vista del orientador sobre su generación.
//
// Qué resuelve: un orientador no necesita "analítica", necesita saber A QUIÉN
// ACOMPAÑAR. Por eso la pantalla abre con las alertas ya redactadas por el
// backend en lenguaje de decisión ("12 se quedaron a medias", "5 no pueden
// entregar evidencias porque falta el permiso del tutor") y sólo después
// muestra los números.
//
// El aislamiento por escuela NO se decide aquí: viaja como rol `plantel:<x>` en
// el JWT y lo aplica el servidor. Si el resumen trae `bloqueado_a_plantel`, ni
// siquiera se pinta el selector — pero aunque se pintara, el servidor filtra.
//
// Tokens semánticos del tema (bg-card, text-muted-foreground…): nunca bg-white.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, CheckCircle2, ExternalLink, FileText, Loader2, Lock,
  Search, ShieldCheck, Users,
} from 'lucide-react';

import { getAlumnos, getDistribucion, getResumen } from '../api';
import type { Alumno, Distribucion, Resumen } from '../types';

const num = (n: number) => new Intl.NumberFormat('es-MX').format(n || 0);

const CONFIANZA: Record<string, { texto: string; clase: string }> = {
  alta: { texto: 'Alta', clase: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  media: { texto: 'Media', clase: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  revisar: { texto: 'Revisar', clase: 'bg-red-500/15 text-red-600 dark:text-red-400' },
};

function Kpi({ etiqueta, valor, pie }: { etiqueta: string; valor: string; pie?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs text-muted-foreground">{etiqueta}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">{valor}</p>
      {pie && <p className="mt-0.5 text-xs text-muted-foreground">{pie}</p>}
    </div>
  );
}

/** Barras de una distribución. Muestra tal cual lo que manda el backend,
 *  incluida la categoría "(muy pocos para mostrar)" del umbral de anonimato. */
function Barras({ titulo, datos }: { titulo: string; datos: Record<string, number> }) {
  const filas = Object.entries(datos || {}).sort((a, b) => b[1] - a[1]);
  const tope = Math.max(1, ...filas.map(([, n]) => n));
  if (!filas.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="mb-3 text-sm font-medium text-foreground">{titulo}</p>
      <div className="space-y-2">
        {filas.map(([k, n]) => (
          <div key={k} className="flex items-center gap-3">
            <span className="w-40 shrink-0 truncate text-xs text-muted-foreground" title={k}>{k}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
              <div className="h-2 rounded-full bg-primary" style={{ width: `${(n / tope) * 100}%` }} />
            </div>
            <span className="w-10 shrink-0 text-right text-xs tabular-nums text-foreground">{num(n)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PlantelPage() {
  const [resumen, setResumen] = useState<Resumen | null>(null);
  const [dist, setDist] = useState<Distribucion | null>(null);
  const [alumnos, setAlumnos] = useState<Alumno[]>([]);
  const [totalAlumnos, setTotalAlumnos] = useState(0);
  const [escuela, setEscuela] = useState('');
  const [estado, setEstado] = useState('');
  const [buscar, setBuscar] = useState('');
  const [pagina, setPagina] = useState(1);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const bloqueado = resumen?.bloqueado_a_plantel || '';

  const cargar = useCallback(async () => {
    setCargando(true); setError(null);
    try {
      const [r, d, l] = await Promise.all([
        getResumen(escuela),
        getDistribucion(escuela),
        getAlumnos({ escuela, estado, buscar, pagina, por_pagina: 25 }),
      ]);
      setResumen(r); setDist(d); setAlumnos(l.alumnos); setTotalAlumnos(l.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setCargando(false); }
  }, [escuela, estado, buscar, pagina]);

  useEffect(() => { void cargar(); }, [cargar]);

  const paginas = useMemo(() => Math.max(1, Math.ceil(totalAlumnos / 25)), [totalAlumnos]);

  if (error && !resumen) {
    return (
      <div className="m-6 rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        <AlertTriangle className="mr-2 inline text-red-500" size={16} /> No se pudo cargar: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* ── Encabezado ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-foreground">
            <Users size={20} /> Mi Plantel
          </h1>
          <p className="text-sm text-muted-foreground">
            Cómo va tu generación en su orientación vocacional.
          </p>
        </div>
        {bloqueado ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-xs text-muted-foreground">
            <Lock size={13} /> {bloqueado}
          </span>
        ) : (resumen?.escuelas?.length ? (
          <select value={escuela} onChange={(e) => { setEscuela(e.target.value); setPagina(1); }}
            className="rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground">
            <option value="">Todas las escuelas</option>
            {resumen.escuelas.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        ) : null)}
      </div>

      {/* ── Alertas primero: es lo accionable ── */}
      {resumen?.alertas?.length ? (
        <div className="space-y-2">
          {resumen.alertas.map((a) => (
            <div key={a} className="flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-foreground">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <span>{a}</span>
            </div>
          ))}
        </div>
      ) : resumen && resumen.total > 0 ? (
        <div className="flex items-center gap-2.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-foreground">
          <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400" />
          Todo en orden: no hay nada pendiente de atender.
        </div>
      ) : null}

      {/* ── KPIs ── */}
      {resumen && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Kpi etiqueta="Estudiantes" valor={num(resumen.total)} />
          <Kpi etiqueta="Terminaron" valor={num(resumen.completados)}
               pie={`${resumen.tasa_completado_pct}% de los que empezaron`} />
          <Kpi etiqueta="A medias" valor={num(resumen.en_curso)} pie="pueden retomar" />
          <Kpi etiqueta="Con permiso del tutor" valor={num(resumen.con_permiso_tutor)}
               pie="pueden entregar evidencias" />
        </div>
      )}

      {/* ── Distribución ── */}
      {dist && (
        <>
          <div className="grid gap-3 md:grid-cols-2">
            <Barras titulo="Hacia dónde apunta la generación" datos={dist.grupo_principal} />
            <Barras titulo="Confianza de las respuestas" datos={dist.confianza} />
            <Barras titulo="Por sexo" datos={dist.por_sexo} />
            <Barras titulo="Por edad" datos={dist.por_edad} />
          </div>
          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <ShieldCheck size={14} className="mt-0.5 shrink-0" />
            {dist.nota}
          </p>
        </>
      )}

      {/* ── Lista de alumnos ── */}
      <div className="rounded-lg border border-border bg-card">
        <div className="flex flex-wrap items-center gap-3 border-b border-border p-4">
          <p className="mr-auto text-sm font-medium text-foreground">
            Estudiantes {totalAlumnos ? <span className="text-muted-foreground">({num(totalAlumnos)})</span> : null}
          </p>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={buscar} onChange={(e) => { setBuscar(e.target.value); setPagina(1); }}
              placeholder="Buscar por nombre"
              className="rounded-lg border border-border bg-background py-2 pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground" />
          </div>
          <select value={estado} onChange={(e) => { setEstado(e.target.value); setPagina(1); }}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground">
            <option value="">Todos</option>
            <option value="completado">Terminaron</option>
            <option value="en_curso">A medias</option>
          </select>
        </div>

        {cargando ? (
          <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
            <Loader2 className="animate-spin" size={16} /> Cargando…
          </div>
        ) : !alumnos.length ? (
          <p className="p-10 text-center text-sm text-muted-foreground">
            Todavía no hay estudiantes que mostrar con estos filtros.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-xs text-muted-foreground">
                <tr>
                  <th className="p-3 text-left font-medium">Estudiante</th>
                  <th className="p-3 text-left font-medium">Avance</th>
                  <th className="p-3 text-left font-medium">Perfil</th>
                  <th className="p-3 text-left font-medium">Confianza</th>
                  <th className="p-3 text-left font-medium">Permiso</th>
                  <th className="p-3 text-right font-medium">Reporte</th>
                </tr>
              </thead>
              <tbody>
                {alumnos.map((a) => {
                  const conf = CONFIANZA[a.confianza];
                  return (
                    <tr key={a.perfil_id} className="border-b border-border/60 last:border-0">
                      <td className="p-3">
                        <p className="font-medium text-foreground">{a.nombre || 'Sin nombre'}</p>
                        <p className="text-xs text-muted-foreground">
                          {[a.escuela, a.grado && `${a.grado} sem`].filter(Boolean).join(' · ')}
                        </p>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-muted">
                            <div className="h-1.5 rounded-full bg-primary" style={{ width: `${a.avance_pct}%` }} />
                          </div>
                          <span className="text-xs tabular-nums text-muted-foreground">
                            {a.respondidos}/{a.total}
                          </span>
                        </div>
                      </td>
                      <td className="p-3">
                        {a.codigo_holland ? (
                          <>
                            <span className="font-mono text-xs font-semibold tracking-wider text-foreground">
                              {a.codigo_holland}
                            </span>
                            {a.grupo_principal && (
                              <p className="text-xs text-muted-foreground">{a.grupo_principal}</p>
                            )}
                          </>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                      <td className="p-3">
                        {conf ? (
                          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${conf.clase}`}>
                            {conf.texto}
                          </span>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                      <td className="p-3">
                        {a.permiso_tutor ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 size={13} /> Sí
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Falta</span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        {a.tiene_reporte ? (
                          <a href={`https://eligeconfuturia.mx/perfil/${a.reporte_token}`}
                             target="_blank" rel="noopener noreferrer"
                             className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                            <FileText size={13} /> Ver <ExternalLink size={11} />
                          </a>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {paginas > 1 && (
          <div className="flex items-center justify-between border-t border-border p-3 text-xs text-muted-foreground">
            <span>Página {pagina} de {paginas}</span>
            <div className="flex gap-2">
              <button disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}
                className="rounded-md border border-border px-3 py-1.5 text-foreground disabled:opacity-40">
                Anterior
              </button>
              <button disabled={pagina >= paginas} onClick={() => setPagina((p) => p + 1)}
                className="rounded-md border border-border px-3 py-1.5 text-foreground disabled:opacity-40">
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Los resultados son un insumo de orientación, no un diagnóstico psicológico.
        Conviene revisarlos con cada estudiante antes de usarlos para decidir.
      </p>
    </div>
  );
}
